import { CartContext } from './context/CartContext';
import {
    useParams
} from 'react-router-dom';
import React, {
    lazy,
    Suspense,
    useContext,
    useEffect,
    useState,
    useMemo,
} from 'react';
import '../styles/ProductAddedToCartOverlay.css';
import AppContext from './context/AppContext';
import PropTypes from 'prop-types';
import LoadingFallback from './LoadingFallback';
import ModalComponentNoButton from './ModalComponentNoButton';
import getSymbolFromCurrency from 'currency-symbol-map';
import useCheckout from '../hooks/useCheckout';
import useDynamicPath from '../hooks/useDynamicPath';
import constants from '../constants.json' assert { type: 'json' };

const ProductOftenBoughtWithProducts = lazy(() => import('./ProductOftenBoughtWithProducts'));

/**
 * Component for showing the product page.
 * @component
 */
const ProductAddedToCartOverlay = ({ isMobile, show, setShow }) => {
    /** Contexts */
    const { appContext } = useContext(AppContext);
    const { setStartCheckout } = useCheckout();
    const { cartState } = useContext(CartContext);

    const subtotal = useMemo(() => cartState
        ?.map((item) => parseFloat(item.price * item.quantity))
        .reduce((prev, cur) => +prev + +cur, 0)
        ?? 0, [ cartState ]);
    const appConfig = appContext.config.themeConfig;
    const { handle } = useParams();
    const [lineItem, setLineItem] = useState(null);
    const productPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.PRODUCT_PATH });

    useEffect(() => {
        if (cartState) {
            setLineItem(cartState.find((lineItem) => lineItem.handle === handle));
        }
    }, [cartState]);

    return (
        <ModalComponentNoButton
            showFooter={false}
            show={show}
            setShow={setShow}
            modalTitle={`OK, ${lineItem?.quantity ?? 0} items were added to your cart. What next?`}
            size={'xl'}
        >
            {!lineItem &&
                <LoadingFallback />
            }
            {lineItem &&
                <div
                    className={'product-added-cart--top-container'}>
                    <div
                        style={{ width: '33%'}}>
                        <img
                            src={lineItem.imageUrl?? appConfig.productNoImageUrl}
                            style={{
                                objectFit: 'cover',
                            }}
                            alt={'No Product Image'}
                            width={'100%'}
                            height={'100%'}
                        />

                    </div>
                    <div
                        className={'product-added-cart--top-middle'}>
                        <div>{lineItem.title}</div>
                        <div>Quantity: {lineItem.quantity} </div>
                        <div
                            className={'fw-bold'}>{getSymbolFromCurrency(appConfig.storeCurrency)}{(lineItem.price * lineItem.quantity).toFixed(2)} </div>
                    </div>
                    <div
                        className={'d-flex flex-column align-items-center'}
                        style={{ width: '33%'}}>
                        <button
                            className={'product-added-cart--checkout-button mb-2'}
                            type={'button'}
                            onClick={setStartCheckout}
                        >PROCEED TO CHECKOUT</button>
                        <div
                            className={'d-flex flex-column align-items-center mb-3'}>
                            <div>Order Subtotal</div>
                            <div
                                className={'fw-bold h1'}>
                                {getSymbolFromCurrency(appConfig.storeCurrency)}
                                {subtotal.toFixed(2)}
                            </div>
                        </div>
                        <div
                            className={'product-added-cart--total-items'}>
                            Your cart contains {cartState?.reduce((accumulator, item) => {
                                return accumulator + item.quantity;
                            }, 0) ?? 0} items
                        </div>
                        <div
                            className={'d-flex flex-column align-items-center'}>
                            <a
                                href={`${productPath[0].pathBeforeParams}/${ handle}`}>
                                <div> CONTINUE SHOPPING</div>
                            </a>
                            <div> <span>OR</span> <a
                                href={'/shoppingcart.asp'}>VIEW OR EDIT YOUR CART</a></div>
                        </div>

                    </div>
                </div>
            }
            <Suspense
                fallback={<LoadingFallback />}
            >
                <ProductOftenBoughtWithProducts
                    isMobile={isMobile}
                    productHandle={handle}
                    title={'You May Also Like...'}
                />
            </Suspense>
        </ModalComponentNoButton>
    );
};

ProductAddedToCartOverlay.propTypes = {
    isMobile: PropTypes.bool,
    show: PropTypes.bool,
    setShow: PropTypes.func,
};

export default ProductAddedToCartOverlay;
